/*
 * @Author: edvardchen
 * @Date:   2016-11-16 21:23:43
 * @Last Modified by: jerryfwang
 * @Last Modified time: 2019-09-09 22:45:43
 */

// @flow
import * as React from 'react';
import autobind from 'core-decorators/lib/autobind';
import { listen } from 'fbjs/lib/EventListener';

import _ from 'lodash';

import styles from './index.scss';

const StatusClass = {
  LOADING: 'is-loading',
  LOADED: 'is-loaded',
  FAILED: 'is-error',
};

const visible = (elm) => {
  const rect = elm.getBoundingClientRect();
  const { top, bottom } = rect;
  return top < window.innerHeight && bottom > 0;
};

const SCROLL_DELAY = 300;

type Props = {
  initPadding?: string,
  autoHW: boolean,
  onLoad?: Function,
  onError?: Function,
  src: string,
  alt?: string,
  isServerRender?: boolean, // 是否需要server端渲染
  imageStyle?: Object,
  className: string,
  children?: React.Node,
  lazy: boolean,
};

type State = {
  padding: string,
  status?: string,
};

export default class PureImage extends React.PureComponent<Props, State> {
  el: ?HTMLElement;
  watchScroll: ?{
    remove: Function,
  };

  static defaultProps = {
    lazy: true,
    autoHW: false,
    className: '',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      padding: props.initPadding || '',
    };
  }
  shouldComponentUpdate() {
    // 防止切换网络图片裂掉
    if (this.state.status === StatusClass.LOADED) {
      return false;
    }
    return true;
  }
  componentDidMount() {
    if (!this.el) {
      return;
    }
    if (!this.props.lazy || visible(this.el)) {
      setTimeout(() => {
        this.setState({ status: StatusClass.LOADING }); // start loading
      });
      return;
    }

    this.watchScroll = listen(
      window,
      'scroll',
      _.throttle(
        () => {
          if (!this.el) {
            this.watchScroll && this.watchScroll.remove();
            return;
          }
          if (visible(this.el)) {
            this.setState({ status: StatusClass.LOADING });
            this.watchScroll && this.watchScroll.remove();
          }
        },
        SCROLL_DELAY,
        { leading: false },
      ),
    );
  }

  componentWillUnmount() {
    this.watchScroll && this.watchScroll.remove();
  }

  @autobind
  onLoad({ currentTarget }: Event) {
    this.setState({ status: StatusClass.LOADED });
    const { autoHW, onLoad } = this.props;
    if (autoHW) {
      // 根据真实高宽调整 padding height
      if (currentTarget instanceof HTMLImageElement) {
        const ratio = currentTarget.naturalHeight / currentTarget.naturalWidth;
        this.setState({
          padding: `${ratio * 100}%`,
        });
      }
    }
    onLoad && onLoad(currentTarget);
  }

  @autobind
  onError({ currentTarget }: Event) {
    this.setState({ status: StatusClass.FAILED });
    const { onError } = this.props;
    onError && onError(currentTarget);
  }
  @autobind
  refFnc(node: *) {
    this.el = node;
    if (this.el) {
      // 防止更新loading状态时未更新到
      this.el.style.paddingBottom = this.state.padding;
    }
  }
  render() {
    let { status } = this.state;
    const { padding } = this.state;
    const { src, alt, children, className, imageStyle = {}, isServerRender } = this.props;
    if (isServerRender) {
      status = status || StatusClass.LOADING;
    }
    let imageJSX;
    if (status) {
      // no status during server rendering
      imageJSX = <img onLoad={this.onLoad} onError={this.onError} alt={alt} src={src} style={imageStyle} />;
    }

    // 没有有 padidng 使用 wrap class
    if (!padding) {
      return (
        <div
          className={`${className} ${styles.wrap} ${styles[status] || ''}`}
          ref={(node) => {
            this.el = node;
          }}
        >
          {imageJSX}
          {children}
        </div>
      );
    }

    return (
      <div
        className={`${className} ${styles.wrapWithPad} ${styles[status] || ''} ${className}`}
        style={{ paddingBottom: padding }}
        ref={(node) => {
          this.refFnc(node);
        }}
      >
        {imageJSX}
        {children}
      </div>
    );
  }
}
